import css from './Form.module.scss';
import { useLang } from '@/i18n';
import { useRouter } from 'next/router';
import ReCAPTCHA from 'react-google-recaptcha';
import { Select, Switch, message } from 'antd';
import { useEffect, useState } from 'react';
import type { CooperationProps } from './interface';
import { getStatesList, getCitiesList, createDealer } from '@/service';
import { INIT_COUNTRY_MAP } from '@/constants/quote';
import type { CityListRes, StateListRes } from './interface';
import { ENGLISH_LANG, rpcMap } from '@/constants';
import gtm from '@/utils/gtm';
interface OptionProps {
    key: number;
    label?: string;
    value?: string | boolean;
    text?: string;
    phoneCode?: string;
}

interface FormItemProps {
    placeholder?: string;
    key: string;
    type: 'input' | 'select' | 'textarea' | 'recaptcha' | 'switch' | 'checkbox' | 'button' | 'radio';
    required?: boolean;
    tip?: string;
    long?: boolean; // UI长文本 独占100%宽度
    options?: OptionProps[];
    disable?: boolean; // true 隐藏 / false 显示
    max?: number; // 最大长度
    rows?: number; // 行数
    text?: string; // 文本
}

interface FormDataProps {
    firstName: string;
    lastName: string;
    country: string | undefined;
    state: string | undefined;
    city: string | undefined;
    zipCode: string;
    phone: string;
    email: string;
    companyName: string;
    businessCategory: string;
    companyType: string;
    companyTypeSpecify?: string;
    licensedElectricianConstructor: boolean | undefined;
    companyWebsite: string;
    productCategory: string;
    address2: string;
    productLineup: string;
    valueProposition: string;
    companyAddress: string;
    cooperationGain: string;
    businessSuccess: string;
    message: string;
    recaptchaResponse: string;
}

interface FormTipProps {
    firstName: boolean;
    lastName: boolean;
    country: boolean;
    state: boolean;
    zipCode: boolean;
    phone: boolean;
    email: boolean;
    companyName: boolean;
    businessCategory: boolean;
    companyType: boolean;
    productCategory: boolean;
    companyAddress: boolean;
    recaptchaResponse: boolean;
    companyWebsite: boolean;
}

interface SelectOpenProps {
    country: boolean;
    state: boolean;
    city: boolean;
    businessCategory: boolean;
    companyType: boolean;
    productCategory: boolean;
}

const RegMap: { [key: string]: RegExp } = {
    firstName: /^((?!\d|\+|&|=|\_|'|-|,|<|>|\.{2,}).){1,30}$/,
    lastName: /^((?!\d|\+|&|=|\_|'|-|,|<|>|\.{2,}).){1,30}$/,
    zipCode: /^[A-Za-z0-9]{0,10}$/,
    phone: /^[0-9+]{7,20}$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    companyWebsite: /^((https|http|ftp|rtsp|mms){0,1}(:\/\/){0,1})www\.(([A-Za-z0-9-~]+)\.)+([A-Za-z0-9-~\/])+$/,
};

const Form: React.FC<CooperationProps> = ({ countryList, callback }) => {
    const SECRET_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY;
    const { t } = useLang();
    const { locale = 'us' } = useRouter();
    const [form, setForm] = useState({} as FormDataProps);
    const [tipForm, setTipForm] = useState({} as FormTipProps);
    const [selectOpen, setSelectOpen] = useState({
        country: false,
        state: false,
        city: false,
        businessCategory: false,
        companyType: false,
        productCategory: false,
    } as SelectOpenProps);
    const hl = rpcMap[ENGLISH_LANG.includes(locale) ? 'en' : locale];
    const [key, setKey] = useState(new Date().getTime());

    const items_static: FormItemProps[] = [
        {
            placeholder: t('G_label_first_name'),
            key: 'firstName',
            type: 'input',
            required: true,
            tip: t('G_tip_first_name'),
        },
        {
            placeholder: t('G_label_last_name'),
            key: 'lastName',
            type: 'input',
            required: true,
            tip: t('G_tip_last_name'),
        },
        {
            placeholder: t('G_label_country'),
            key: 'country',
            type: 'select',
            required: true,
            tip: t('G_tip_country'),
            options: [] as OptionProps[],
        },
        {
            placeholder: t('G_label_state'),
            key: 'state',
            type: 'select',
            required: true,
            tip: t('G_tip_state'),
            options: [] as OptionProps[],
        },
        {
            placeholder: t('G_label_city'),
            key: 'city',
            type: 'select',
            options: [] as OptionProps[],
        },
        {
            placeholder: t('G_label_zip_code'),
            key: 'zipCode',
            type: 'input',
            required: true,
            tip: t('G_tip_zip_code'),
        },
        {
            placeholder: t('G_label_phone_number'),
            key: 'phone',
            type: 'input',
            required: true,
            tip: t('G_tip_phone_number'),
        },
        {
            placeholder: t('G_label_email'),
            key: 'email',
            type: 'input',
            required: true,
            tip: t('G_tip_email'),
        },
        {
            placeholder: t('G_label_company_name'),
            key: 'companyName',
            type: 'input',
            required: true,
            tip: t('G_tip_company_name'),
        },
        {
            placeholder: t('G_label_business_category'),
            key: 'businessCategory',
            type: 'select',
            required: true,
            tip: t('G_tip_business_category'),
            options: Array.from({ length: 14 }, (_, i) => ({
                key: i + 1,
                label: t(`G_option_business_category_${i + 1}`),
                value: t(`G_option_business_category_${i + 1}`),
            })) as OptionProps[],
        },
        {
            placeholder: t('G_label_company_type'),
            key: 'companyType',
            type: 'select',
            required: true,
            tip: t('G_tip_company_type'),
            options: Array.from({ length: 12 }, (_, i) => ({
                key: i + 1,
                label: t(`G_option_company_type_${i + 1}`),
                value: t(`G_option_company_type_${i + 1}`),
            })) as OptionProps[],
        },
        {
            placeholder: t('G_label_company_type_specify'),
            key: 'companyTypeSpecify',
            type: 'input',
            disable: true,
        },
        {
            placeholder: t('G_label_license'),
            key: 'licensedElectricianConstructor',
            type: 'select',
            options: Array.from({ length: 2 }, (_, i) => ({
                key: i + 1,
                label: t(`G_option_license_${i + 1}`),
                value: Boolean(i === 0),
            })) as OptionProps[],
        },
        {
            placeholder: t('G_label_product_category'),
            key: 'productCategory',
            type: 'select',
            required: true,
            long: true,
            tip: t('G_tip_product_category'),
            options: Array.from({ length: 11 }, (_, i) => ({
                key: i + 1,
                label: t(`G_option_product_category_${i + 1}`),
                value: t(`G_option_product_category_${i + 1}`),
            })) as OptionProps[],
        },
        {
            placeholder: t('G_label_company_address'),
            key: 'companyAddress',
            type: 'input',
            required: true,
            tip: t('G_tip_company_address'),
            long: true,
        },
        {
            placeholder: t('G_label_address_2'),
            key: 'address2',
            type: 'input',
            long: true,
        },
        {
            placeholder: t('G_label_focus'),
            key: 'productLineup',
            type: 'textarea',
            long: true,
            required: true,
            tip: t('G_tip_focus'),
        },
        {
            placeholder: t('G_label_company_website'),
            key: 'companyWebsite',
            type: 'input',
            long: true,
            tip: t('G_tip_company_website'),
        },
        {
            placeholder: t('G_label_value_proposition'),
            key: 'valueProposition',
            type: 'textarea',
            long: true,
        },
        {
            placeholder: t('G_label_hope_gain'),
            key: 'cooperationGain',
            type: 'textarea',
            long: true,
        },
        {
            placeholder: t('G_label_define_success'),
            key: 'businessSuccess',
            type: 'textarea',
            long: true,
        },
        {
            placeholder: t('G_label_message'),
            key: 'message',
            type: 'textarea',
            long: true,
            rows: 6,
        },
        {
            key: 'recaptchaResponse',
            type: 'recaptcha',
            required: true,
            tip: t('G_tip_recaptcha'),
        },
        {
            key: 'submit',
            type: 'button',
            long: true,
            text: t('G_button_submit'),
        },
    ];

    const [items, setItems] = useState<FormItemProps[]>(items_static);

    const handleFormChange = (e: any, key: string, type: string) => {
        if (['input', 'textarea'].includes(type)) {
            return setForm({
                ...form,
                [key]: e.target.value,
            });
        }
        if (['select', 'switch', 'recaptcha'].includes(type)) {
            if (type === 'select') {
                if (key === 'country') {
                    form.state = undefined;
                    form.city = undefined;
                }
                if (key === 'state') {
                    form.city = undefined;
                }
            }
            if (tipForm[key as keyof FormTipProps]) {
                setTipForm({
                    ...tipForm,
                    [key]: false,
                });
            }
            return setForm({
                ...form,
                [key]: e,
            });
        }
        if (type === 'button' && key === 'submit') {
            return handleSubmit();
        }
    };

    const handleOnFocus = (key: string) => {
        if (tipForm[key as keyof FormTipProps]) {
            setTipForm({
                ...tipForm,
                [key]: false,
            });
        }
    };

    const handleValidate = () => {
        let tipObj: FormTipProps = {
            firstName: false,
            lastName: false,
            country: false,
            state: false,
            zipCode: false,
            phone: false,
            email: false,
            companyName: false,
            businessCategory: false,
            companyType: false,
            productCategory: false,
            companyAddress: false,
            recaptchaResponse: false,
            companyWebsite: false,
        };
        const needValiProps: string[] = items?.filter((item: FormItemProps) => item.required)?.map((item: FormItemProps) => item.key);
        needValiProps.push('companyWebsite');
        needValiProps?.map((key: string) => {
            const value = form[key as keyof FormDataProps];
            if (key === 'companyWebsite') {
                tipObj[key] = value ? !RegMap[key].test(String(value)) : false;
            } else {
                tipObj[key] = Boolean(!value || (RegMap[key] && !RegMap[key].test(String(value))));
            }
        });
        setTipForm(tipObj);
        return Object.values(tipObj)?.every((v: boolean) => !v);
    };

    const handleSubmit = async () => {
        if (handleValidate()) {
            const res = await createDealer(form);
            if (res?.code === '0') {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'become_dealer_submit',
                    status: 'success',
                });
                message.success(res.message || 'Success');
                setForm((prev) => {
                    return { country: prev.country } as FormDataProps;
                });
                setTipForm((_) => {
                    return {} as FormTipProps;
                });
                setKey(new Date().getTime());
                callback();
            } else {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'become_dealer_submit',
                    status: 'fail',
                });
                message.error(res.message);
            }
        }
    };

    const getStates = async (countryId: number) => {
        try {
            const res: StateListRes = await getStatesList({
                countryId,
                page: 1,
                size: 300,
            });
            if (res.code === '0') {
                const stateList: OptionProps[] = res.data?.content?.map((item: any) => ({
                    key: item.id,
                    label: item.name,
                    value: item.name,
                }));
                setItems((prevItems: FormItemProps[]) => {
                    const newItems = JSON.parse(JSON.stringify(prevItems));
                    const targetState = newItems.find((item: FormItemProps) => item.key === 'state');
                    if (targetState) {
                        targetState.options = stateList;
                    }
                    const targetCity = newItems.find((item: FormItemProps) => item.key === 'city');
                    if (targetCity) {
                        targetCity.options = [] as OptionProps[];
                    }
                    return newItems;
                });
            } else {
                message.error(res.message);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const getCity = async (stateId: number) => {
        try {
            const res: CityListRes = await getCitiesList({
                stateId,
                page: 1,
                size: 3000,
            });
            if (res.code === '0') {
                const cityList: OptionProps = res.data?.content?.map((item: any) => ({
                    key: item.id,
                    label: item.name,
                    value: item.name,
                }));
                setItems((prevItems: FormItemProps[]) => {
                    const newItems = JSON.parse(JSON.stringify(prevItems));
                    const targetCity = newItems.find((item: FormItemProps) => item.key === 'city');
                    if (targetCity) {
                        targetCity.options = cityList;
                    }
                    return newItems;
                });
            } else {
                message.error(res.message);
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        if (countryList?.length > 0) {
            const countryOptions: OptionProps[] = countryList
                ?.filter((item: any) => item.text && item.key && item.phoneCode)
                .map(
                    (country: any) =>
                        country?.text && {
                            key: country.key,
                            label: country.text,
                            value: country.text,
                        },
                );
            const newItems = JSON.parse(JSON.stringify(items));
            const target = newItems.find((item: FormItemProps) => item.key === 'country');
            if (target) {
                target.options = countryOptions;
            }
            setItems(newItems);
            const targetCountry = countryOptions?.find((item: any) => item.value === INIT_COUNTRY_MAP[locale]?.name);
            setForm({
                ...form,
                country: form?.country || targetCountry?.value || '',
            });
        }
    }, [countryList]);

    useEffect(() => {
        const targetCountry = countryList?.find((country: any) => country.text === form.country);
        if (targetCountry) {
            getStates(targetCountry.key);
        }
    }, [form.country]);

    useEffect(() => {
        const stateList: OptionProps[] = items.find((item: FormItemProps) => item.key === 'state')?.options || [];
        const targetState = stateList?.find((state: any) => state.value === form.state);
        if (targetState) {
            getCity(targetState.key);
        }
    }, [form.state]);

    useEffect(() => {
        setItems((prevItems: FormItemProps[]) => {
            const newItems = JSON.parse(JSON.stringify(prevItems));
            const target = newItems.find((item: FormItemProps) => item.key === 'companyTypeSpecify');
            if (target) {
                target.disable = form.companyType !== t('G_option_company_type_12');
            }
            return newItems;
        });
    }, [form.companyType]);

    return (
        <div className={css.form}>
            <div className="info">
                <h2 className="title">{t('G_title')}</h2>
                <p className="desc">{t('G_desc')}</p>
            </div>
            <ul className="form-box" key={key + '0'}>
                {items?.map((item: FormItemProps) => (
                    <li className={`form-item${item.long ? ' long' : ''}${item.disable ? ' disable' : ''} form-item-${item.type}`} key={item.key}>
                        {item.type === 'input' && (
                            <>
                                {item.required && !form[item.key as keyof FormDataProps] && <span className="red-star">*</span>}
                                <input
                                    type="text"
                                    className="item-cont input"
                                    placeholder={(item?.required ? '  ' : '') + item.placeholder}
                                    value={form[item.key as keyof FormDataProps]}
                                    onChange={(e: any) => handleFormChange(e, item.key, item.type)}
                                    onFocus={() => handleOnFocus(item.key)}
                                />
                            </>
                        )}
                        {item.type === 'select' && (
                            <Select
                                className={`item-cont select${item?.required ? ' required' : ''}`}
                                placeholder={(item?.required ? '  ' : '') + item.placeholder}
                                value={form[item.key as keyof FormDataProps]}
                                showSearch
                                getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                onDropdownVisibleChange={(open: boolean) =>
                                    setSelectOpen({
                                        ...selectOpen,
                                        [item.key]: open,
                                    })
                                }
                                suffixIcon={
                                    <svg
                                        className="select-arrow"
                                        style={{
                                            transform: `rotate(${selectOpen[item.key as keyof SelectOpenProps] ? '180deg' : '0deg'})`,
                                        }}
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="17"
                                        viewBox="0 0 16 17"
                                        fill="none"
                                    >
                                        <path
                                            d="M13.641 6.75651L8.30766 12.0898L2.97432 6.75651"
                                            stroke="#000"
                                            stroke-width="1.4"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                }
                                dropdownClassName="ef-select-item"
                                onChange={(e: any) => handleFormChange(e, item.key, item.type)}
                            >
                                {item.options?.map((option: OptionProps) => (
                                    <option value={option.value} key={item.key + '_' + option.value + '_' + option.key}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        )}
                        {item.type === 'textarea' && (
                            <>
                                {item.required && !form[item.key as keyof FormDataProps] && <span className="red-star">*</span>}
                                <textarea
                                    className={`item-cont textarea${item?.required ? ' required' : ''}`}
                                    placeholder={(item?.required ? '  ' : '') + item.placeholder}
                                    value={form[item.key as keyof FormDataProps]}
                                    onChange={(e: any) => handleFormChange(e, item.key, item.type)}
                                    onFocus={() => handleOnFocus(item.key)}
                                    maxLength={item?.max || 2000}
                                    rows={item?.rows || 3}
                                />
                                <p className="text-num">
                                    {form[item.key as keyof FormDataProps] ? form[item.key as keyof FormDataProps]?.length : 0}
                                    {' / '}
                                    {item?.max || 2000}
                                </p>
                            </>
                        )}
                        {item.type === 'switch' && (
                            <div className="item-cont switch">
                                <span className="item-label">{(item?.required ? '* ' : '') + item.placeholder}</span>
                                <Switch
                                    className="switch-box"
                                    checked={form.licensedElectricianConstructor}
                                    onChange={(e: any) => handleFormChange(e, item.key, item.type)}
                                />
                            </div>
                        )}
                        {item.type === 'recaptcha' && (
                            <div className="item-cont recaptcha">
                                <ReCAPTCHA key={key} hl={hl} sitekey={SECRET_KEY || ''} onChange={(e: any) => handleFormChange(e, item.key, item.type)} />
                            </div>
                        )}
                        {item.type === 'button' && (
                            <button className="item-cont button" onClick={() => handleFormChange('', item.key, item.type)}>
                                {item?.text}
                            </button>
                        )}
                        {item?.tip && tipForm[item.key as keyof FormTipProps] && <p className="form-item-tip">{item.tip}</p>}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default Form;
